import { GlAlert } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { cloneDeep } from 'lodash-es';
import { produce } from 'immer';
import { __ } from '~/locale';
import wikiPageQuery from '~/wikis/graphql/wiki_page.query.graphql';
import SkeletonNote from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/notes/skeleton_note.vue?vue3';
import wikiDiscussionSortOrder from '~/wikis/wiki_notes/graphql/wiki_discussion_sort_order.query.graphql';
import { WIKI_NOTES_SORT_ORDER } from '~/wikis/constants';
import WikiPageAwardEmoji from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/components/wiki_page_award_emoji.vue?vue3';
import OrderedLayout from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/ordered_layout.vue?vue3';
import PlaceholderNote from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/placeholder_note.vue?vue3';
import WikiNotesActivityHeader from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/wiki_notes_activity_header.vue?vue3';
import WikiCommentForm from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/wiki_comment_form.vue?vue3';
import WikiDiscussion from '/builds/gitlab/gitlab-ee/app/assets/javascripts/wikis/wiki_notes/components/wiki_discussion.vue?vue3';
export default {
    i18n: {
        loadingFailedErrText: __('Something went wrong while fetching comments. Please refresh the page.'),
        retryText: __('Retry')
    },
    name: 'WikiNotesApp',
    components: {
        GlAlert,
        WikiCommentForm,
        WikiDiscussion,
        WikiNotesActivityHeader,
        WikiPageAwardEmoji,
        OrderedLayout,
        SkeletonNote,
        PlaceholderNote
    },
    inject: [
        'noteCount',
        'queryVariables'
    ],
    apollo: {
        wikiDiscussionSortOrder,
        wikiPage: {
            query: wikiPageQuery,
            variables () {
                return this.queryVariables;
            },
            update (data) {
                return data?.wikiPage?.discussions?.nodes || [];
            },
            error () {
                this.loadingFailed = true;
                return [];
            },
            result ({ data }) {
                this.noteableId = data?.wikiPage?.id || '';
                this.discussions = cloneDeep(data?.wikiPage?.discussions?.nodes) || [];
                this.userPermissions = data?.wikiPage?.userPermissions || {};
                this.pageAwardEmoji = data?.wikiPage?.awardEmoji?.nodes || [];
                this.isPageSubscribed = data?.wikiPage?.subscribed ?? false;
            }
        }
    },
    data () {
        return {
            // eslint-disable-next-line vue/no-unused-properties -- needed for `wikiPage` Apollo query
            wikiPage: {},
            noteableId: '',
            userPermissions: {},
            pageAwardEmoji: [],
            isPageSubscribed: false,
            loadingFailed: false,
            placeholderNote: {},
            discussions: Array.from({
                length: this.noteCount
            }, (_, index)=>({
                    id: index,
                    isSkeletonNote: true
                })),
            wikiDiscussionSortOrder: null
        };
    },
    computed: {
        slotKeys () {
            switch(this.wikiDiscussionSortOrder){
                case WIKI_NOTES_SORT_ORDER.CREATED_DESC:
                    return [
                        'form',
                        'place-holder-note',
                        'comments'
                    ];
                case WIKI_NOTES_SORT_ORDER.CREATED_ASC:
                default:
                    return [
                        'comments',
                        'place-holder-note',
                        'form'
                    ];
            }
        },
        wikiPageData () {
            return this.$apollo.queries.wikiPage;
        },
        isLoading () {
            return this.$apollo.queries.wikiPage.loading;
        },
        sortedDiscussions () {
            if (!this.wikiDiscussionSortOrder) return this.discussions;
            return [
                ...this.discussions
            ].sort((a, b)=>{
                const [minuend, subtrahend] = this.wikiDiscussionSortOrder === WIKI_NOTES_SORT_ORDER.CREATED_ASC ? [
                    a,
                    b
                ] : [
                    b,
                    a
                ];
                const minuendFirstNoteDate = minuend.notes?.nodes[0]?.createdAt;
                const subtrahendFirstNoteDate = subtrahend.notes?.nodes[0]?.createdAt;
                if (!minuendFirstNoteDate || !subtrahendFirstNoteDate) {
                    return 0; // Keep original order for items without valid dates
                }
                return new Date(minuendFirstNoteDate).getTime() - new Date(subtrahendFirstNoteDate).getTime();
            });
        }
    },
    methods: {
        setPlaceHolderNote (note) {
            this.placeholderNote = note;
        },
        removePlaceholder () {
            this.placeholderNote = {};
        },
        getDiscussionKey (key, stringModifier) {
            return [
                key,
                stringModifier
            ].join('-');
        },
        handleDeleteNote (noteId, discussionId) {
            const discussionIndex = this.discussions.findIndex((discussion)=>discussion.id === discussionId);
            if (discussionIndex === -1) return;
            const discussion = this.discussions[discussionIndex];
            const isLastNote = discussion.notes.nodes.length === 1;
            // Update local state
            if (isLastNote) {
                // Remove entire discussion if it's the last note
                this.discussions = this.discussions.filter(({ id })=>id !== discussionId);
            } else {
                // Remove only the specific note
                this.discussions[discussionIndex].notes.nodes = discussion.notes.nodes.filter(({ id })=>id !== noteId);
            }
            this.updateCache({
                discussionId,
                noteId,
                isLastNote
            });
        },
        updateCache ({ discussion, discussionId, noteId, isLastNote }) {
            if (!this.$apollo.provider) return;
            const { defaultClient: cache } = this.$apollo.provider.clients;
            const queryData = cache.readQuery({
                query: wikiPageQuery,
                variables: this.queryVariables
            });
            if (!queryData) return;
            let data;
            if (discussion) {
                data = produce(queryData, (draft)=>{
                    draft.wikiPage.discussions.nodes.push({
                        ...discussion,
                        replyId: null,
                        resolvable: false,
                        resolved: false,
                        resolvedAt: null,
                        resolvedBy: null
                    });
                });
            } else {
                data = produce(queryData, (draft)=>{
                    const cachedDiscussionIndex = draft.wikiPage.discussions.nodes.findIndex((d)=>d.id === discussionId);
                    if (cachedDiscussionIndex === -1) return;
                    if (isLastNote) {
                        // Remove entire discussion if it's the last note
                        draft.wikiPage.discussions.nodes = draft.wikiPage.discussions.nodes.filter((d)=>d.id !== discussionId);
                    } else {
                        // Remove only the specific note
                        draft.wikiPage.discussions.nodes[cachedDiscussionIndex].notes.nodes = draft.wikiPage.discussions.nodes[cachedDiscussionIndex].notes.nodes.filter((note)=>note.id !== noteId);
                    }
                });
            }
            cache.writeQuery({
                query: wikiPageQuery,
                variables: this.queryVariables,
                data
            });
        }
    }
};
